import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getCurrentDateInBarbados, getISODateInBarbados } from '../../../utils/timezone';

const OCCUPATION_OPTIONS = [
  { value: '', label: 'Select occupation' },
  { value: 'Software Engineer', label: 'Software Engineer' },
  { value: 'Product Manager', label: 'Product Manager' },
  { value: 'Designer', label: 'Designer' },
  { value: 'Data Analyst', label: 'Data Analyst' },
  { value: 'Marketing Manager', label: 'Marketing Manager' },
  { value: 'Sales Representative', label: 'Sales Representative' },
  { value: 'Teacher', label: 'Teacher' },
  { value: 'Healthcare Professional', label: 'Healthcare Professional' },
  { value: 'Accountant', label: 'Accountant' },
  { value: 'Consultant', label: 'Consultant' },
  { value: 'Entrepreneur', label: 'Entrepreneur' },
  { value: 'Student', label: 'Student' },
  { value: 'Retired', label: 'Retired' },
  { value: 'custom', label: 'Other' }
];

const ProfileSection = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    username: '',
    birthday: '',
    occupation: '',
    customOccupation: '',
    employer: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [age, setAge] = useState(null);
  const [showCustomOccupation, setShowCustomOccupation] = useState(false);

  useEffect(() => {
    if (userProfile) {
      const isCustomOccupation = userProfile?.occupation && 
        !OCCUPATION_OPTIONS?.some(opt => opt?.value === userProfile?.occupation && opt?.value !== 'custom');
      
      setFormData({
        fullName: userProfile?.full_name || '',
        email: user?.email || '',
        username: userProfile?.username || '',
        birthday: userProfile?.birthday || '',
        occupation: isCustomOccupation ? 'custom' : (userProfile?.occupation || ''),
        customOccupation: isCustomOccupation ? userProfile?.occupation : '',
        employer: userProfile?.employer || ''
      });
      setShowCustomOccupation(isCustomOccupation);
      setAvatarPreview(userProfile?.avatar_url || null);
      
      if (userProfile?.birthday) {
        calculateAge(userProfile?.birthday);
      }
    }
  }, [userProfile, user]);

  const calculateAge = (birthday) => {
    if (!birthday) {
      setAge(null);
      return;
    }
    const birthDate = new Date(birthday);
    const today = getCurrentDateInBarbados();
    let calculatedAge = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      calculatedAge--;
    }
    
    setAge(calculatedAge >= 0 ? calculatedAge : null);
  };

  const handleBirthdayChange = (e) => {
    const birthday = e?.target?.value;
    setFormData({ ...formData, birthday });
    
    if (!birthday) {
      setAge(null);
      return;
    }
    const birthDate = new Date(birthday);
    const today = getCurrentDateInBarbados();
    let calculatedAge = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      calculatedAge--;
    }
    
    setAge(calculatedAge >= 0 ? calculatedAge : null);
  };

  const handleOccupationChange = (value) => {
    setFormData({ ...formData, occupation: value });
    setShowCustomOccupation(value === 'custom');
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      if (!file?.type?.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !user) return null;
    
    setUploadingAvatar(true);
    try {
      const fileExt = avatarFile?.name?.split('.')?.pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;
      
      const { error: uploadError } = await supabase?.storage
        ?.from('user-avatars')
        ?.upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) throw uploadError;
      
      const { data: urlData } = supabase?.storage
        ?.from('user-avatars')
        ?.getPublicUrl(filePath);
      
      return urlData?.publicUrl;
    } catch (err) {
      console.error('Avatar upload error:', err);
      throw new Error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!formData?.fullName?.trim()) {
      setError('Full name is required');
      return;
    }

    if (formData?.username && formData?.username?.trim()?.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    setLoading(true);
    try {
      let avatarUrl = userProfile?.avatar_url;
      
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }
      
      const finalOccupation = formData?.occupation === 'custom' 
        ? formData?.customOccupation?.trim() 
        : formData?.occupation;
      
      const updates = {
        full_name: formData?.fullName?.trim(),
        username: formData?.username?.trim() || null,
        birthday: formData?.birthday || null,
        occupation: finalOccupation || null,
        employer: formData?.employer?.trim() || null
      };
      
      if (avatarUrl) {
        updates.avatar_url = avatarUrl;
      }

      const { error: updateError } = await updateProfile(updates);

      if (updateError) {
        if (updateError?.message?.includes('duplicate') || updateError?.message?.includes('unique')) {
          setError('Username is already taken. Please choose another.');
        } else {
          setError(updateError?.message || 'Failed to update profile');
        }
      } else {
        setSuccess('Profile updated successfully');
        setAvatarFile(null);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-20 md:p-24">
      <div className="flex items-center gap-12 mb-20">
        <Icon name="User" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-semibold text-card-foreground">Profile Information</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-24">
        Update your personal information and profile details
      </p>
      {success && (
        <div className="mb-20 p-16 bg-success/10 border border-success/20 rounded-lg flex items-start gap-12">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-sm text-success flex-1">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-20 p-16 bg-error/10 border border-error/20 rounded-lg flex items-start gap-12">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          <p className="text-sm text-error flex-1">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-20">
        {/* Avatar Upload */}
        <div className="space-y-12">
          <label className="block text-sm font-medium text-foreground">
            Profile Photo <span className="text-muted-foreground">(Optional)</span>
          </label>
          <div className="flex items-center gap-16">
            <div className="relative">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Profile preview"
                  className="w-24 h-24 rounded-full object-cover border-2 border-border"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center border-2 border-border">
                  <Icon name="User" size={32} color="var(--color-muted-foreground)" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                id="avatar-upload"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <label
                htmlFor="avatar-upload"
                className="inline-flex items-center gap-8 px-16 py-8 bg-muted hover:bg-muted/80 text-foreground rounded-lg cursor-pointer transition-smooth text-sm font-medium"
              >
                <Icon name="Upload" size={16} />
                Choose Photo
              </label>
              <p className="text-xs text-muted-foreground mt-8">JPG, PNG or GIF. Max 5MB.</p>
            </div>
          </div>
        </div>

        <Input
          label="Full Name"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
          required
        />

        <Input
          label="Username"
          placeholder="Choose a unique username"
          value={formData?.username}
          onChange={(e) => setFormData({ ...formData, username: e?.target?.value })}
          description="This will be your display name across the app"
        />

        <Input
          label="Email Address"
          type="email"
          value={formData?.email}
          disabled
          description="Email cannot be changed"
        />

        {/* Birthday with Age Display */}
        <div className="space-y-8">
          <Input
            label="Birthday"
            type="date"
            value={formData?.birthday}
            onChange={handleBirthdayChange}
            max={getISODateInBarbados()}
            description="Your age will be calculated automatically"
          />
          {age !== null && (
            <div className="flex items-center gap-8 text-sm text-muted-foreground">
              <Icon name="Calendar" size={16} />
              <span>Age: <strong className="text-foreground">{age} years old</strong></span>
            </div>
          )}
        </div>

        {/* Occupation with Custom Option */}
        <div className="space-y-12">
          <Select
            label="Occupation"
            value={formData?.occupation}
            onChange={handleOccupationChange}
            options={OCCUPATION_OPTIONS}
          />
          {showCustomOccupation && (
            <Input
              placeholder="Enter your occupation"
              value={formData?.customOccupation}
              onChange={(e) => setFormData({ ...formData, customOccupation: e?.target?.value })}
            />
          )}
        </div>

        {/* Employer */}
        <Input
          label="Employer"
          type="text"
          value={formData?.employer}
          onChange={(e) => setFormData({ ...formData, employer: e?.target?.value })}
          placeholder="Enter your employer (optional)"
        />

        <div className="flex justify-end pt-16">
          <Button
            type="submit"
            variant="default"
            loading={loading || uploadingAvatar}
            iconName="Save"
            iconPosition="left"
          >
            {uploadingAvatar ? 'Uploading...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSection;